// zr-core/scheduling — pure, framework-agnostic scheduled-call descriptor. Lets an app
// plan a group call ("Schedule group calls"), share an invite, and decide when the room
// goes live. No timers and no I/O: callers pass an explicit `now` (epoch ms) so the logic
// is deterministic and unit-testable; the app owns reminders/notifications.

import { generatePeerId } from './index';

export type ScheduledCallStatus = 'upcoming' | 'live' | 'ended';

export interface ScheduledCall {
  /** Stable id; also usable as the room id when the call starts. */
  id: string;
  roomId: string;
  title: string;
  /** Host's participant id. */
  host: string;
  /** Epoch ms the call is scheduled to start. */
  startsAt: number;
  /** Planned duration in minutes (drives the `ended` window + calendar entry). */
  durationMins: number;
  /** Invited participant ids (informational; the room itself gates entry). */
  invitees: string[];
  /** Optional room password carried into the eventual join. */
  password?: string;
  /** True for a "silent" group call nobody is rung for — people drop in. */
  silent?: boolean;
  description?: string;
}

export interface CreateScheduledCallInput {
  title: string;
  host: string;
  startsAt: number;
  durationMins?: number;
  invitees?: string[];
  roomId?: string;
  password?: string;
  silent?: boolean;
  description?: string;
}

/** Build a normalized ScheduledCall, generating a room id when one isn't supplied. */
export function createScheduledCall(input: CreateScheduledCallInput): ScheduledCall {
  if (!input.title || !input.title.trim()) throw new Error('createScheduledCall: title is required.');
  if (!Number.isFinite(input.startsAt)) throw new Error('createScheduledCall: startsAt must be epoch ms.');
  const id = input.roomId || generatePeerId('call');
  return {
    id,
    roomId: id,
    title: input.title.trim(),
    host: input.host,
    startsAt: input.startsAt,
    durationMins: input.durationMins && input.durationMins > 0 ? input.durationMins : 60,
    invitees: input.invitees ? [...new Set(input.invitees)] : [],
    password: input.password,
    silent: input.silent,
    description: input.description,
  };
}

/**
 * Where a scheduled call sits relative to `now`. A small `joinWindowMs` lets people in
 * a little early (default 5 min), matching Meet/Zoom "join before host" behaviour.
 */
export function scheduledCallStatus(
  call: Pick<ScheduledCall, 'startsAt' | 'durationMins'>,
  now: number,
  joinWindowMs = 5 * 60_000,
): ScheduledCallStatus {
  const endsAt = call.startsAt + call.durationMins * 60_000;
  if (now >= endsAt) return 'ended';
  if (now >= call.startsAt - joinWindowMs) return 'live';
  return 'upcoming';
}

/** Ms until the call's join window opens (0 once it is live, negative-clamped to 0). */
export function msUntilJoinable(
  call: Pick<ScheduledCall, 'startsAt'>,
  now: number,
  joinWindowMs = 5 * 60_000,
): number {
  return Math.max(0, call.startsAt - joinWindowMs - now);
}

/** Serialize for transport/storage (round-trips through deserializeScheduledCall). */
export function serializeScheduledCall(call: ScheduledCall): string {
  return JSON.stringify(call);
}

export function deserializeScheduledCall(raw: string): ScheduledCall {
  const c = JSON.parse(raw) as ScheduledCall;
  if (!c || typeof c.id !== 'string' || typeof c.startsAt !== 'number') {
    throw new Error('deserializeScheduledCall: malformed payload.');
  }
  return c;
}

function icsDate(ms: number): string {
  // YYYYMMDDTHHMMSSZ in UTC — the iCalendar UTC form.
  return new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

function icsEscape(text: string): string {
  return text.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
}

/**
 * Render an RFC-5545 VEVENT so an app can attach a real calendar invite ("Plan and send
 * an invite"). `joinUrl` becomes the event location/URL. The DTSTAMP is taken from the
 * call's start (deterministic — no hidden clock read), which is fine for an invite.
 */
export function toICS(call: ScheduledCall, joinUrl?: string): string {
  const endsAt = call.startsAt + call.durationMins * 60_000;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ZeroRate RTC//Scheduled Call//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `UID:${call.id}@zerorate.rtc`,
    `DTSTAMP:${icsDate(call.startsAt)}`,
    `DTSTART:${icsDate(call.startsAt)}`,
    `DTEND:${icsDate(endsAt)}`,
    `SUMMARY:${icsEscape(call.title)}`,
    call.description ? `DESCRIPTION:${icsEscape(call.description)}` : undefined,
    joinUrl ? `URL:${icsEscape(joinUrl)}` : undefined,
    joinUrl ? `LOCATION:${icsEscape(joinUrl)}` : undefined,
    `ORGANIZER:CN=${icsEscape(call.host)}`,
    ...call.invitees.map((id) => `ATTENDEE;ROLE=REQ-PARTICIPANT:${icsEscape(id)}`),
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);
  return lines.join('\r\n');
}
